import * as React from "react";

import { formControlClassName } from "@/components/ui/form-field";
import { cn } from "@/lib/utils";

function Input({
  className,
  type,
  value,
  onChange,
  onFocus,
  onBlur,
  ...props
}: React.ComponentProps<"input">) {
  // Controlled number inputs often convert an empty string to 0 in their
  // onChange handler. Keep the text being edited locally so clearing "0" and
  // entering "10" does not produce the awkward intermediate value "010".
  const [numberDraft, setNumberDraft] = React.useState<string | null>(null);
  const isNumberEditing = React.useRef(false);
  const isControlledNumber = type === "number" && value !== undefined;

  React.useEffect(() => {
    if (
      !isControlledNumber ||
      isNumberEditing.current ||
      numberDraft === null
    ) {
      return;
    }

    const parentRepresentsEmptyAsZero =
      numberDraft === "" && Number(value) === 0;
    if (!parentRepresentsEmptyAsZero) {
      setNumberDraft(null);
    }
  }, [isControlledNumber, numberDraft, value]);

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (isControlledNumber) {
      isNumberEditing.current = true;
    }
    onFocus?.(event);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isControlledNumber) {
      setNumberDraft(event.currentTarget.value);
    }
    onChange?.(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    isNumberEditing.current = false;
    onBlur?.(event);

    if (!isControlledNumber) return;

    const parentRepresentsEmptyAsZero =
      event.currentTarget.value === "" && Number(value) === 0;
    setNumberDraft(parentRepresentsEmptyAsZero ? "" : null);
  };

  return (
    <input
      type={type}
      value={isControlledNumber && numberDraft !== null ? numberDraft : value}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      data-slot="input"
      className={cn(
        formControlClassName,
        "file:text-foreground selection:bg-primary selection:text-primary-foreground placeholder:text-muted-foreground flex h-9 px-3 py-1 text-base file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium md:text-sm",
        className
      )}
      {...props}
    />
  );
}

export { Input };
